import React, { useState, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronRight, MapPin } from 'lucide-react'
import MapLocationPopupCN from 'components/popups/MapLocationPopupCN'
import LocationActionSheet from 'screens/locations/components/Locations/LocationActionSheet'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table'

const headerCells = [
	{ id: 'image', label: '', sortable: false },
	{ id: 'assetName', label: 'Location', sortable: true },
	{ id: 'address', label: 'Address', sortable: true },
	{ id: 'city', label: 'City', sortable: true },
	{ id: 'stateProvince', label: 'State', sortable: true },
	{ id: 'postalCode', label: 'Zip Code', sortable: true },
	{ id: 'edit', label: 'Edit', sortable: false },
]

const getNestedValue = (obj, path) => {
	return path.split('.').reduce((acc, key) => acc?.[key], obj)
}

const PortfolioLocationsTable = ({
	portfolioName,
	locations,
	recordCount,
	userId,
	isOwner,
	onRemoveFromPortfolio,
}) => {
	const [orderBy, setOrderBy] = useState(null)
	const [order, setOrder] = useState('asc')
	const [sheetOpen, setSheetOpen] = useState(false)
	const [sheetLocation, setSheetLocation] = useState(null)

	// Map popup state
	const [openMap, setOpenMap] = useState(false)
	const [mapLocation, setMapLocation] = useState(null)

	const handleOpenMap = (e, loc) => {
		e.stopPropagation()
		setMapLocation(loc)
		setOpenMap(true)
	}

	const currentRoute = useLocation()
	const oldPath = currentRoute.pathname

	const handleSort = columnId => {
		if (orderBy === columnId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const sortedLocations = useMemo(() => {
		if (!orderBy) return locations

		return [...locations].sort((a, b) => {
			const aVal = (getNestedValue(a, orderBy) || '').toString().toLowerCase()
			const bVal = (getNestedValue(b, orderBy) || '').toString().toLowerCase()

			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [locations, orderBy, order])

	const SortIcon = ({ columnId }) => {
		if (orderBy !== columnId) return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ArrowUp className="ml-1 h-3 w-3 text-primary" />
		) : (
			<ArrowDown className="ml-1 h-3 w-3 text-primary" />
		)
	}

	return (
		<div>
			<Table>
				<TableHeader>
					<TableRow className="border-b-2 border-[#027fff] hover:bg-transparent">
						{headerCells.map(cell => (
							<TableHead
								key={cell.id}
								className={[
									cell.sortable ? 'cursor-pointer select-none' : '',
									'text-gray-700 text-xs font-semibold whitespace-nowrap',
									cell.id === 'edit' ? 'w-12 text-center' : '',
								].join(' ')}
								onClick={cell.sortable ? () => handleSort(cell.id) : undefined}
							>
								<span className="inline-flex items-center">
									{cell.label}
									{cell.sortable && <SortIcon columnId={cell.id} />}
								</span>
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{sortedLocations.map(location => {
						if (!location) return null
						return (
							<TableRow key={location._id} className="h-7 hover:bg-[#fffbf2]">
								<TableCell className="w-12 py-1 px-2">
									<Avatar className="h-7 w-7">
										<AvatarImage src={location.image} />
										<AvatarFallback />
									</Avatar>
								</TableCell>
								<TableCell className="py-1">
									<Link
										to={`/locations/${location._id}`}
										state={{ backButtonTitle: portfolioName, backButtonLink: oldPath }}
										className="text-primary hover:underline font-medium text-sm"
									>
										{location.assetName}
									</Link>
								</TableCell>
								<TableCell className="py-1 text-sm">
									<button
										type="button"
										onClick={e => handleOpenMap(e, location)}
										className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs text-gray-600 hover:bg-[#0080fe]/10 hover:text-[#0080fe] hover:border-[#0080fe]/20 transition-colors cursor-pointer"
									>
										<MapPin className="h-3 w-3" />
										{location.address}
									</button>
								</TableCell>
								<TableCell className="py-1 text-sm">{location.city}</TableCell>
								<TableCell className="py-1 text-sm">{location.stateProvince}</TableCell>
								<TableCell className="py-1 text-sm">{location.postalCode}</TableCell>
								<TableCell className="w-12 py-1 text-center">
									<button
										type="button"
										className="text-gray-400 hover:text-gray-700 transition-colors p-1 rounded hover:bg-gray-100"
										onClick={e => {
											e.stopPropagation()
											setSheetLocation(location)
											setSheetOpen(true)
										}}
										title="Edit"
									>
										<ChevronRight className="h-4 w-4" />{' '}
									</button>
								</TableCell>
							</TableRow>
						)
					})}
				</TableBody>
			</Table>

			<LocationActionSheet
				location={sheetLocation}
				open={sheetOpen}
				onClose={() => setSheetOpen(false)}
				onDelete={
					onRemoveFromPortfolio && sheetLocation
						? async () => {
								await onRemoveFromPortfolio(sheetLocation)
						  }
						: undefined
				}
				deleteLabel="Remove from Portfolio"
				deleteConfirmText="Remove this location from the portfolio?"
			/>

			{openMap && mapLocation && (
				<MapLocationPopupCN
					open={openMap}
					onClose={() => setOpenMap(false)}
					location={mapLocation}
				/>
			)}
		</div>
	)
}

export default PortfolioLocationsTable
